// The ONLY place that creates an AudioContext (IMPLEMENTATION §2.9 "Audio unlock", arch test).
// Capture-phase listeners on pointerup/touchend/click/keydown. On the first gesture:
//   1. navigator.audioSession.type = 'ambient' | 'playback' (guarded; iOS 16.4+), BEFORE the context exists;
//   2. new AudioContext({latencyHint:'interactive'});  3. resume();  4. play a 1-sample silent buffer;
//   5. onUnlock(ctx) synchronously (build buffers, play the sound for this same gesture).
// Stays armed while the state isn't 'running' (also iOS 'interrupted'); suspends when hidden.
// M9 (additive): `handle.onUnlock(cb)` subscribers (called at once when already unlocked), `onRegesture(cb)`
// (a later gesture resumed a context that wasn't running: back from the background, iOS 'interrupted'),
// `setSessionType` (the Settings silent-switch toggle) and `unlockNow()` (the same unlock, run from inside a
// gesture handler that fired before the capture listener, e.g. an input handler registered earlier).
// Lead (M9 × M11 wiring): `hold(on)` — the page-lifecycle mute (hidden, another tab owns the game, the lock is
// lost): suspends the context and keeps every gesture and `visible` from creating or resuming it until released.

export type SessionType = 'ambient' | 'playback';

export interface AudioUnlockOptions {
  /** 'ambient' respects the iPhone silent switch (default); 'playback' plays through it. */
  sessionType: SessionType;
  onUnlock?(ctx: AudioContext): void;
}

export interface AudioHandle {
  ctx(): AudioContext | null;
  /** 'none' before the first gesture, 'unsupported' without Web Audio, else the context state. */
  state(): string;
  /** navigator.audioSession.type as set (null when the API is missing). */
  session(): string | null;
  armed(): boolean;
  /** Runs `cb(ctx)` synchronously at the unlock (at once when already unlocked). Returns an unsubscribe. */
  onUnlock(cb: (ctx: AudioContext) => void): () => void;
  /**
   * Runs `cb(ctx)` synchronously each time a gesture re-unlocks an existing context that isn't running
   * (resume() + silence were just called: the sound of that same gesture may be scheduled). Unsubscribe fn.
   */
  onRegesture(cb: (ctx: AudioContext) => void): () => void;
  /**
   * Silent-switch mode. Before the unlock it only changes what step 1 sets; afterwards it sets
   * navigator.audioSession.type at once (guarded). Real-iOS behaviour of a live change is a USER GATE.
   */
  setSessionType(t: SessionType): void;
  /** The gesture unlock, run now. Call ONLY from inside a user-gesture handler (iOS needs the activation). */
  unlockNow(): void;
  /**
   * Lifecycle hold (M11: hidden, ElsewhereOverlay, lock lost): `true` suspends the context and disarms; while
   * held neither a gesture nor `visible` creates or resumes it. `false` resumes it (when visible) and re-arms.
   */
  hold(on: boolean): void;
  held(): boolean;
  destroy(): void;
}

interface AudioSessionLike {
  type: string;
}

type AudioCtor = new (opts?: AudioContextOptions) => AudioContext;

const GESTURES = ['pointerup', 'touchend', 'click', 'keydown'] as const;

export function armAudio(win: Window, opts: AudioUnlockOptions): AudioHandle {
  let ctx: AudioContext | null = null;
  let unsupported = false;
  let armed = false;
  let destroyed = false;
  let held = false;
  let session: string | null = null;
  let sessionType: SessionType = opts.sessionType;
  const subscribers: ((ctx: AudioContext) => void)[] = [];
  const regesture: ((ctx: AudioContext) => void)[] = [];
  if (opts.onUnlock) subscribers.push(opts.onUnlock);
  const cap = { capture: true } as const;
  const subscribe =
    (list: ((ctx: AudioContext) => void)[]) =>
    (cb: (ctx: AudioContext) => void): (() => void) => {
      list.push(cb);
      return () => {
        const i = list.indexOf(cb);
        if (i >= 0) list.splice(i, 1);
      };
    };

  const nav = win.navigator as Navigator & { audioSession?: AudioSessionLike };
  // iOS 16+ / Chrome 107+ have the unprefixed constructor; Playwright WebKit on Windows has none.
  const host = win as Window & { AudioContext?: AudioCtor };

  const applySession = (): void => {
    if (!nav.audioSession) return;
    try {
      nav.audioSession.type = sessionType;
      session = nav.audioSession.type;
    } catch {
      session = null;
    }
  };

  const playSilence = (c: AudioContext): void => {
    const buf = c.createBuffer(1, 1, c.sampleRate);
    const src = c.createBufferSource();
    src.buffer = buf;
    src.connect(c.destination);
    src.start(0);
  };

  const disarm = (): void => {
    if (!armed) return;
    armed = false;
    for (const t of GESTURES) win.removeEventListener(t, onGesture, cap);
  };

  const arm = (): void => {
    if (armed || unsupported || destroyed || held) return;
    armed = true;
    for (const t of GESTURES) win.addEventListener(t, onGesture, cap);
  };

  function onGesture(): void {
    if (destroyed || held) return;
    if (!ctx) {
      applySession(); // step 1: before the context exists
      if (typeof host.AudioContext !== 'function') {
        unsupported = true;
        disarm();
        return;
      }
      const c = new host.AudioContext({ latencyHint: 'interactive' });
      ctx = c;
      c.addEventListener('statechange', () => {
        if (c.state === 'running') disarm();
        else if (win.document.visibilityState === 'visible' && !held) arm();
      });
      void c.resume().catch(() => undefined);
      playSilence(c);
      for (const cb of [...subscribers]) cb(c);
    } else if (ctx.state !== 'running') {
      void ctx.resume().catch(() => undefined);
      playSilence(ctx);
      for (const cb of [...regesture]) cb(ctx);
    }
    if (ctx.state === 'running') disarm();
  }

  const onVisibility = (): void => {
    if (!ctx) return;
    if (win.document.visibilityState === 'hidden') {
      void ctx.suspend().catch(() => undefined);
    } else if (!held) {
      void ctx.resume().catch(() => undefined);
      arm();
    }
  };

  arm();
  win.document.addEventListener('visibilitychange', onVisibility);

  return {
    ctx: () => ctx,
    state: () => (unsupported ? 'unsupported' : ctx ? ctx.state : 'none'),
    session: () => session,
    armed: () => armed,
    onUnlock(cb) {
      const off = subscribe(subscribers)(cb);
      if (ctx) cb(ctx);
      return off;
    },
    onRegesture: subscribe(regesture),
    setSessionType(t) {
      sessionType = t;
      if (ctx) applySession();
    },
    unlockNow() {
      if (!unsupported) onGesture();
    },
    hold(on) {
      if (held === on || destroyed) return;
      held = on;
      if (on) {
        disarm();
        void ctx?.suspend().catch(() => undefined);
      } else if (win.document.visibilityState === 'visible') {
        void ctx?.resume().catch(() => undefined);
        if (ctx?.state !== 'running') arm(); // iOS: a resume outside a gesture may not take; the next tap does it
      }
    },
    held: () => held,
    destroy() {
      disarm();
      destroyed = true;
      subscribers.length = 0;
      regesture.length = 0;
      win.document.removeEventListener('visibilitychange', onVisibility);
      void ctx?.close().catch(() => undefined);
      ctx = null;
    },
  };
}
